import {useMemo, type ReactNode} from 'react'
import {Box} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {detectOS, type OS} from '../utils/detectOS'

const TRAFFIC_LIGHT_RED = '#FF5F57'
const TRAFFIC_LIGHT_YELLOW = '#FEBC2E'
const TRAFFIC_LIGHT_GREEN = '#28C840'

const WIN_BUTTON_FG = 'rgba(255, 255, 255, 0.55)'
const YARU_BUTTON_BG = '#3a3a3a'
const YARU_BUTTON_FG = 'rgba(255, 255, 255, 0.85)'
const YARU_CLOSE_BG = '#E95420'

interface WindowControlsProps {
  // Slot to render. Returns null when the OS doesn't draw controls in this slot.
  position: 'left' | 'right'
  // Width of an empty spacer used to keep the title centered. Defaults to 64.
  spacerWidth?: number
  onClose?: () => void
  onMinimize?: () => void
  onMaximize?: () => void
}

interface ActionHandlers {
  onClose?: () => void
  onMinimize?: () => void
  onMaximize?: () => void
}

const WindowControls = ({position, spacerWidth = 64, onClose, onMinimize, onMaximize}: WindowControlsProps) => {
  const os = useMemo<OS>(() => detectOS(), [])
  const handlers: ActionHandlers = {onClose, onMinimize, onMaximize}

  if (os === 'macos') {
    return position === 'left' ? <MacControls {...handlers} /> : <Spacer width={spacerWidth} />
  }

  if (position === 'left') return <Spacer width={spacerWidth} />
  return os === 'windows' ? <WindowsControls {...handlers} /> : <LinuxControls {...handlers} />
}

const Spacer = ({width}: {width: number}) => <Box sx={{flex: '0 0 auto', width}} />

const MacControls = ({onClose, onMinimize, onMaximize}: ActionHandlers) => {
  const {t} = useTranslation()
  return (
    <Box sx={macStyles.row}>
      <ActionDot color={TRAFFIC_LIGHT_RED} onClick={onClose} label={t('a11y.windowClose')} />
      <ActionDot color={TRAFFIC_LIGHT_YELLOW} onClick={onMinimize} label={t('a11y.windowMinimize')} />
      <ActionDot color={TRAFFIC_LIGHT_GREEN} onClick={onMaximize} label={t('a11y.windowMaximize')} />
    </Box>
  )
}

interface ActionDotProps {
  color: string
  onClick?: () => void
  label: string
}

const ActionDot = ({color, onClick, label}: ActionDotProps) => {
  if (!onClick) return <Box sx={{...macStyles.dot, background: color}} />
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{...macStyles.dot, ...macStyles.dotButton, background: color}}
    />
  )
}

const WindowsControls = ({onClose, onMinimize, onMaximize}: ActionHandlers) => {
  const {t} = useTranslation()
  return (
    <Box sx={winStyles.row}>
      <WinButton onClick={onMinimize} label={t('a11y.windowMinimize')}>
        <MinimizeGlyph />
      </WinButton>
      <WinButton onClick={onMaximize} label={t('a11y.windowMaximize')}>
        <MaximizeGlyph />
      </WinButton>
      <WinButton onClick={onClose} label={t('a11y.windowClose')} closeStyle>
        <CloseGlyph />
      </WinButton>
    </Box>
  )
}

interface ChromeButtonProps {
  onClick?: () => void
  label: string
  closeStyle?: boolean
  children: ReactNode
}

const WinButton = ({onClick, label, closeStyle, children}: ChromeButtonProps) => {
  const baseStyle = {...winStyles.button, ...(closeStyle ? winStyles.closeButton : {})}
  if (!onClick) {
    return (
      <Box sx={baseStyle} aria-hidden>
        {children}
      </Box>
    )
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{...baseStyle, ...winStyles.buttonInteractive}}
    >
      {children}
    </Box>
  )
}

const MinimizeGlyph = () => (
  <svg width={10} height={10} viewBox="0 0 10 10" style={{color: WIN_BUTTON_FG}}>
    <line x1={1} y1={5} x2={9} y2={5} stroke="currentColor" strokeWidth={1.2} strokeLinecap="square" />
  </svg>
)

const MaximizeGlyph = () => (
  <svg width={10} height={10} viewBox="0 0 10 10" style={{color: WIN_BUTTON_FG}}>
    <rect x={1} y={1} width={8} height={8} fill="none" stroke="currentColor" strokeWidth={1.2} />
  </svg>
)

const LinuxControls = ({onClose, onMinimize, onMaximize}: ActionHandlers) => {
  const {t} = useTranslation()
  return (
    <Box sx={linuxStyles.row}>
      <LinuxButton onClick={onMinimize} label={t('a11y.windowMinimize')}>
        <YaruMinimizeGlyph />
      </LinuxButton>
      <LinuxButton onClick={onMaximize} label={t('a11y.windowMaximize')}>
        <YaruMaximizeGlyph />
      </LinuxButton>
      <LinuxButton onClick={onClose} label={t('a11y.windowClose')} closeStyle>
        <YaruCloseGlyph />
      </LinuxButton>
    </Box>
  )
}

const LinuxButton = ({onClick, label, closeStyle, children}: ChromeButtonProps) => {
  const baseStyle = {...linuxStyles.button, ...(closeStyle ? linuxStyles.closeButton : {})}
  if (!onClick) {
    return (
      <Box sx={baseStyle} aria-hidden>
        {children}
      </Box>
    )
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{...baseStyle, ...linuxStyles.buttonInteractive}}
    >
      {children}
    </Box>
  )
}

const CloseGlyph = ({small}: {small?: boolean} = {}) => {
  const size = small ? 8 : 10
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" style={{color: WIN_BUTTON_FG}}>
      <path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" strokeWidth={1.2} fill="none" strokeLinecap="round" />
    </svg>
  )
}

const YaruMinimizeGlyph = () => (
  <svg width={10} height={10} viewBox="0 0 10 10" style={{color: YARU_BUTTON_FG}}>
    <line x1={2} y1={8} x2={8} y2={8} stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" />
  </svg>
)

const YaruMaximizeGlyph = () => (
  <svg width={10} height={10} viewBox="0 0 10 10" style={{color: YARU_BUTTON_FG}}>
    <rect x={2} y={2} width={6} height={6} fill="none" stroke="currentColor" strokeWidth={1.2} rx={0.5} />
  </svg>
)

const YaruCloseGlyph = () => (
  <svg width={10} height={10} viewBox="0 0 10 10" style={{color: YARU_BUTTON_FG}}>
    <path
      d="M2.5 2.5 L7.5 7.5 M7.5 2.5 L2.5 7.5"
      stroke="currentColor"
      strokeWidth={1.2}
      fill="none"
      strokeLinecap="round"
    />
  </svg>
)

const macStyles = {
  row: {
    display: 'flex',
    gap: '7px',
    alignItems: 'center',
    flex: '0 0 auto',
    width: 64,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  dotButton: {
    padding: 0,
    border: 0,
    cursor: 'pointer',
    transition: 'transform 0.12s ease, filter 0.15s ease',
    '&:hover': {
      filter: 'brightness(1.15)',
      transform: 'scale(1.08)',
    },
    '&:focus-visible': {
      outline: '2px solid rgba(255, 255, 255, 0.4)',
      outlineOffset: 2,
    },
  },
} as const

const winStyles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    height: 24,
    marginRight: '-6px',
  },
  button: {
    width: 30,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: WIN_BUTTON_FG,
  },
  closeButton: {
    color: WIN_BUTTON_FG,
  },
  buttonInteractive: {
    background: 'transparent',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '&:focus-visible': {
      outline: '2px solid rgba(255, 255, 255, 0.4)',
      outlineOffset: -2,
    },
  },
} as const

const linuxStyles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    gap: '6px',
    paddingRight: '4px',
  },
  button: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: YARU_BUTTON_BG,
    boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.25), 0 1px 0 rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    background: YARU_CLOSE_BG,
    boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.25), 0 1px 0 rgba(255, 255, 255, 0.1)',
  },
  buttonInteractive: {
    padding: 0,
    cursor: 'pointer',
    transition: 'filter 0.15s ease, transform 0.12s ease',
    '&:hover': {
      filter: 'brightness(1.18)',
      transform: 'scale(1.06)',
    },
    '&:focus-visible': {
      outline: '2px solid rgba(255, 255, 255, 0.4)',
      outlineOffset: 2,
    },
  },
} as const

export default WindowControls
